import { Link } from 'react-router-dom'
import { formatPrice, formatNumber, formatNeighborhoodName } from '../utils/formatters'
import { usePremium } from '../hooks/usePremium'
import { InlineSpinner } from './LoadingSpinner'
import LockIcon from './LockIcon'
import { PAUSE_SINGLE_REPORT } from '../config'
import { DaysToSellMini } from './DaysToSellSection'
import ListingVoteWidget from './ListingVoteWidget'

const _STADIA_KEY = import.meta.env.VITE_STADIA_API_KEY

function getMapThumbnailUrl(lat, lon, markerColor = '2b7cee', zoom = 15) {
  if (!lat || !lon) return null
  const key = _STADIA_KEY ? `&api_key=${_STADIA_KEY}` : ''
  return `https://tiles.stadiamaps.com/static/alidade_smooth.png?center=${lat},${lon}&zoom=${zoom}&size=600x220@2x&m=${lat},${lon},,${markerColor}${key}`
}

/**
 * PropertyPredictionCard component displays a single property prediction in a grid/list.
 * Used for browsing precomputed predictions.
 *
 * 3-layer rendering:
 *   Guest (not logged in): predicted price + address + basic details. No badges. CTA to sign up.
 *   Free (logged in):      + confidence badge (label only) + deal badge (label only). CTA to upgrade.
 *   Paid (buyer/pro):      + confidence range, numeric intervals, relisted badge, full details.
 *
 * @param {Object} props
 * @param {Object} props.prediction - Prediction object from API
 * @param {Function} [props.onClick] - Optional click handler (for selection/highlighting)
 * @param {Function} [props.onExpand] - Optional handler to expand to full property details
 * @param {boolean} [props.forceShowConfidenceBadge] - If true, show confidence badge when data present (e.g. landing preview), even for guests
 */
export default function PropertyPredictionCard({ prediction, onClick, onExpand, forceShowConfidenceBadge = false, cardIndex = 0, voteAggregates }) {
  const { isGuest, showBadges, showBadgeDetails, showFullAnalysis, loading } = usePremium()
  const effectiveShowBadgeDetails = showBadgeDetails || PAUSE_SINGLE_REPORT
  const effectiveShowFullAnalysis = showFullAnalysis || PAUSE_SINGLE_REPORT
  const canShowBadge = showBadges || forceShowConfidenceBadge || PAUSE_SINGLE_REPORT

  if (!prediction || !prediction.predicted_price) {
    return null
  }

  const {
    address,
    city,
    neighbourhood,
    predicted_price,
    confidence_lower,
    confidence_upper,
    bedrooms_total,
    bathrooms_total,
    living_area,
    property_type,
    days_on_market,
    list_price,
    estimated_features,
    neighborhood_confidence_tier,
    composite_confidence,
    house_style,
    neighbourhood_style_sales_count,
    // Over/underpriced flags (returned for free+ even when list_price is stripped)
    is_overpriced,
    is_underpriced,
    // Relist tracking fields
    is_relisted,
    relist_date,
    relist_price,
    list_date,
    // Prediction guardrails
    guardrail_flags,
    guardrail_message,
    // Coordinates for map thumbnail
    lat,
    lon,
  } = prediction
  
  // Helper to check if a feature is estimated
  const estimatedList = estimated_features || []
  const isEstimated = (feature) => estimatedList.includes(feature)
  const hasAnyEstimated = estimatedList.length > 0

  // Unusual property type detection — flag property types with typically limited training data
  const UNUSUAL_STYLES = ['duplex', 'triplex', 'fourplex', 'mobile', 'manufactured', 'modular', 'mobile home', 'cottage', 'log home', 'log']
  const styleLower = (house_style || '').toLowerCase().trim()
  const isUnusualStyle = UNUSUAL_STYLES.some(s => styleLower.includes(s))
  const LOW_SALES_THRESHOLD = 10
  const hasLowSalesCount = neighbourhood_style_sales_count != null && neighbourhood_style_sales_count < LOW_SALES_THRESHOLD

  // Helper function to render PRIMARY composite confidence badge (high/medium/low)
  // This is the main confidence indicator combining model uncertainty + neighborhood reliability
  const getCompositeConfidenceBadge = (confidence) => {
    const label = confidence.charAt(0).toUpperCase() + confidence.slice(1) + ' Confidence'
    
    switch (confidence) {
      case 'high':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-bold bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400" title="High confidence: Narrow prediction interval + reliable neighborhood data">
            <span className="material-symbols-outlined text-xs mr-1">verified</span>
            {effectiveShowBadgeDetails ? label : <span className="flex items-center gap-1">Confidence <LockIcon size="xs" className="opacity-50" /></span>}
          </span>
        )
      case 'medium':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-bold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400" title="Medium confidence: Moderate prediction interval">
            <span className="material-symbols-outlined text-xs mr-1">check_circle</span>
            {effectiveShowBadgeDetails ? label : <span className="flex items-center gap-1">Confidence <LockIcon size="xs" className="opacity-50" /></span>}
          </span>
        )
      case 'low':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-bold bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400" title="Low confidence: Wide prediction interval - use with caution">
            <span className="material-symbols-outlined text-xs mr-1">warning</span>
            {effectiveShowBadgeDetails ? label : <span className="flex items-center gap-1">Confidence <LockIcon size="xs" className="opacity-50" /></span>}
          </span>
        )
      default:
        return null
    }
  }

  // Helper function to render SECONDARY neighborhood tier badge (smaller, provides context)
  // Explains why confidence might be higher/lower based on neighborhood historical accuracy
  // Colors match the tier: high=green, moderate=blue, low=amber, very_low=red
  const getNeighborhoodTierBadge = (tier) => {
    switch (tier) {
      case 'high':
        return (
          <span className="inline-flex items-center px-1 py-0.5 rounded-lg text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400" title="Neighborhood has excellent historical prediction accuracy">
            Area: High
          </span>
        )
      case 'moderate':
        return (
          <span className="inline-flex items-center px-1 py-0.5 rounded-lg text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400" title="Neighborhood has moderate historical prediction accuracy">
            Area: Mod
          </span>
        )
      case 'low':
        return (
          <span className="inline-flex items-center px-1 py-0.5 rounded-lg text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400" title="Neighborhood has limited historical data">
            Area: Low
          </span>
        )
      case 'very_low':
        return (
          <span className="inline-flex items-center px-1 py-0.5 rounded-lg text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400" title="Neighborhood has insufficient historical data">
            Area: V.Low
          </span>
        )
      default:
        return null
    }
  }

  // Calculate price difference if list price exists
  // Use relist_price if relisted, otherwise use list_price
  const currentListPrice = is_relisted && relist_price ? relist_price : list_price
  const priceDiff = currentListPrice ? predicted_price - currentListPrice : null
  const priceDiffPercent = currentListPrice && priceDiff !== null ? ((priceDiff / currentListPrice) * 100).toFixed(1) : null

  // Calculate relist price change if this is a relisted property
  const relistPriceChange = is_relisted && list_price && relist_price 
    ? relist_price - list_price 
    : null
  const relistPriceChangePct = relistPriceChange !== null && list_price > 0
    ? ((relistPriceChange / list_price) * 100).toFixed(1)
    : null

  // Map thumbnail — marker color: green=underpriced, red=overpriced, amber=relisted, blue=neutral
  // Alternate zoom: even cards show neighbourhood context (15), odd cards show street level (17)
  const markerColor = is_relisted ? 'f59e0b'
    : (list_price ? priceDiff > 0 : is_underpriced) ? '16a34a'
    : (list_price ? priceDiff < 0 : is_overpriced) ? 'dc2626'
    : '2b7cee'
  const mapZoom = 14
  const mapThumbnailUrl = getMapThumbnailUrl(lat, lon, markerColor, mapZoom)

  // Calculate days on market from list_date if days_on_market not provided
  const calculatedDom = (() => {
    if (days_on_market !== null && days_on_market !== undefined) return days_on_market
    if (!list_date) return null
    try {
      const listDateObj = new Date(list_date)
      if (isNaN(listDateObj.getTime())) return null
      const diffTime = new Date() - listDateObj
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
      return diffDays >= 0 ? diffDays : null
    } catch {
      return null
    }
  })()

  return (
    <div
      onClick={onClick}
      className={`bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-200 ${
        onClick ? 'cursor-pointer hover:border-primary' : ''
      }`}
    >
      {/* Price Header */}
      <div className="p-4 sm:p-5 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 relative">
        {/* Expand to full report button */}
        {onExpand && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onExpand(prediction)
            }}
            className="absolute top-3 right-3 p-1.5 rounded-lg bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600 hover:border-primary dark:hover:border-primary transition-all shadow-sm group"
            title="View full report"
          >
            <span className="material-symbols-outlined text-lg text-slate-500 dark:text-slate-400 group-hover:text-primary">open_in_full</span>
          </button>
        )}
        <div className="flex flex-col gap-1.5 pr-10">
          <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-wide">
            Predicted Price
          </p>
          <p className="text-2xl lg:text-3xl font-black text-navy-deep dark:text-white leading-tight">
            {formatPrice(predicted_price)}
          </p>
          <DaysToSellMini prediction={prediction} className="text-xs text-slate-600 dark:text-slate-300 leading-snug" />
          {/* List price + over/underpriced — inline row below predicted price */}
          {!loading && (list_price || is_overpriced || is_underpriced) && (
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {/* List price — relist_price takes over with original struck through */}
              {(list_price || (is_relisted && relist_price)) && (
                <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                  <span className="font-semibold uppercase tracking-wide">List:</span>
                  {is_relisted && relist_price && list_price ? (
                    <>
                      <span className="line-through text-slate-400 dark:text-slate-500">{formatPrice(list_price)}</span>
                      <span className="font-bold text-slate-700 dark:text-slate-200">{formatPrice(relist_price)}</span>
                    </>
                  ) : (
                    <span className="font-bold text-slate-700 dark:text-slate-200">{formatPrice(list_price)}</span>
                  )}
                </span>
              )}
              {/* Separator dot */}
              {(list_price || (is_relisted && relist_price)) && (is_overpriced || is_underpriced || priceDiff !== null) && (
                <span className="text-slate-300 dark:text-slate-600 text-xs">·</span>
              )}
              {/* Over/underpriced % marker */}
              <span className={`text-xs font-bold flex items-center gap-0.5 ${
                (list_price ? priceDiff < 0 : is_overpriced) ? 'text-red-500 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'
              }`}>
                {(list_price ? priceDiff < 0 : is_overpriced) ? '↑' : '↓'}
                {effectiveShowFullAnalysis && list_price ? (
                  <>{Math.abs(priceDiffPercent)}% {priceDiff < 0 ? 'over' : 'under'}</>
                ) : (
                  <span className="flex items-center gap-0.5">
                    {(list_price ? priceDiff < 0 : is_overpriced) ? 'Overpriced' : 'Underpriced'}
                    {!effectiveShowFullAnalysis && <LockIcon size="xs" className="opacity-50" />}
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
        {loading && (
          <div className="mt-3 flex items-center gap-2 text-slate-500 dark:text-slate-400">
            <InlineSpinner size="sm" />
            <span className="text-xs font-medium">Loading...</span>
          </div>
        )}
        {/* Confidence Range — paid only (showFullAnalysis) */}
        {!loading && effectiveShowFullAnalysis && confidence_lower && confidence_upper && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
            Range: {formatPrice(confidence_lower)} - {formatPrice(confidence_upper)}
          </p>
        )}
        {/* Confidence Badges — free + paid (showBadges), or when forceShowConfidenceBadge (e.g. landing preview) */}
        {!loading && canShowBadge && (neighborhood_confidence_tier || composite_confidence) && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {/* Primary: Neighbourhood Accuracy Tier (from OOF sold-data — most meaningful signal) */}
            {(neighborhood_confidence_tier && neighborhood_confidence_tier !== 'unknown')
              ? getCompositeConfidenceBadge(
                  neighborhood_confidence_tier === 'high' ? 'high'
                  : neighborhood_confidence_tier === 'moderate' ? 'medium'
                  : 'low'
                )
              : composite_confidence
              ? getCompositeConfidenceBadge(composite_confidence)
              : null
            }
            {/* Secondary: Composite Confidence (model CI) — hidden for now, re-enable if needed */}
            {/* {showFullAnalysis && composite_confidence && getCompositeConfidenceBadge(composite_confidence)} */}
          </div>
        )}
        {/* Unusual property type / limited data warning — premium only */}
        {!loading && effectiveShowFullAnalysis && (isUnusualStyle || hasLowSalesCount) && (
          <div className="mt-2 flex items-center gap-1.5 px-2 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <span className="material-symbols-outlined text-amber-500 dark:text-amber-400 text-sm">warning</span>
            <span className="text-xs text-amber-700 dark:text-amber-400">
              {isUnusualStyle && <span className="font-semibold capitalize">{house_style}</span>}
              {isUnusualStyle && hasLowSalesCount && ' — '}
              {hasLowSalesCount && 'Limited data'}
              {isUnusualStyle && !hasLowSalesCount && ' — Limited comparable data'}
            </span>
          </div>
        )}
        {/* Prediction guardrail warning — when prediction is outside neighbourhood/city bounds */}
        {!loading && Array.isArray(guardrail_flags) && guardrail_flags.length > 0 && guardrail_message && (() => {
          const hardFlags = ['above_neighbourhood_p99', 'above_city_max', 'above_city_p99', 'above_ppsf_p95', 'above_size_bracket_p99', 'below_minimum_floor', 'high_uncertainty_under_prediction']
          const isHard = guardrail_flags.some(f => hardFlags.includes(f))
          return (
            <div className={`mt-2 flex items-start gap-1.5 px-2 py-1.5 rounded-lg border ${isHard ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'}`}>
              <span className={`material-symbols-outlined ${isHard ? 'text-red-500 dark:text-red-400' : 'text-amber-500 dark:text-amber-400'} text-sm mt-0.5 shrink-0`}>{isHard ? 'gpp_maybe' : 'warning'}</span>
              <span className={`text-xs leading-relaxed ${isHard ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
                {guardrail_message}
              </span>
            </div>
          )
        })()}
        {/* Dive deeper one-liner — shown for all non-premium users */}
        {!loading && !effectiveShowFullAnalysis && (
          <div className="mt-2">
            <button
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary/70 hover:text-primary hover:underline"
              onClick={(e) => { e.stopPropagation(); onExpand ? onExpand(prediction) : null }}
            >
              Learn more about this listing
              <span className="material-symbols-outlined text-xs">arrow_forward</span>
            </button>
          </div>
        )}
        {/* Relisted Property Badge — show label for everyone, percentage for paid only */}
        {!loading && is_relisted && (
          <div className="mt-2">
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400" title={`Originally listed ${list_date ? new Date(list_date).toLocaleDateString() : 'earlier'}`}>
              <span className="material-symbols-outlined text-xs mr-0.5">refresh</span>
              Relisted
              {effectiveShowFullAnalysis && relistPriceChangePct !== null && (
                <span className={`ml-1 ${parseFloat(relistPriceChangePct) < 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                  ({relistPriceChangePct > 0 ? '+' : ''}{relistPriceChangePct}%)
                </span>
              )}
              {isGuest && !PAUSE_SINGLE_REPORT && <LockIcon size="xs" className="ml-1 opacity-50" />}
            </span>
          </div>
        )}
      </div>

      {/* Map Thumbnail */}
      {mapThumbnailUrl && (
        <div className="h-40 overflow-hidden bg-slate-100 dark:bg-slate-800">
          <img
            src={mapThumbnailUrl}
            alt={`Map of ${address}`}
            className="w-full h-full object-cover object-center"
            loading="lazy"
            onError={(e) => { e.currentTarget.parentElement.style.display = 'none' }}
          />
        </div>
      )}

      {/* Property Details — always visible (address, beds/baths, sqft are shown to all tiers) */}
      <div className="p-4 sm:p-5">
        {/* Address */}
        <div className="mb-4">
          <p className="text-sm font-semibold text-navy-deep dark:text-white truncate mb-1">
            {address || 'Address not available'}
          </p>
          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            {neighbourhood && (
              <>
                <span>{formatNeighborhoodName(neighbourhood)}</span>
                <span>•</span>
              </>
            )}
            <span>{city}</span>
          </div>
        </div>

        {/* Property Features */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {bedrooms_total && (
            <div className="flex flex-col">
              <span className="text-xs text-slate-500 dark:text-slate-400 font-semibold mb-1">Bedrooms</span>
              <span className="text-sm font-bold text-navy-deep dark:text-white">
                {bedrooms_total}{isEstimated('bedrooms') && <span className="text-xs text-slate-400 align-super">*</span>}
              </span>
            </div>
          )}
          {bathrooms_total && (
            <div className="flex flex-col">
              <span className="text-xs text-slate-500 dark:text-slate-400 font-semibold mb-1">Bathrooms</span>
              <span className="text-sm font-bold text-navy-deep dark:text-white">
                {bathrooms_total}{isEstimated('bathrooms') && <span className="text-xs text-slate-400 align-super">*</span>}
              </span>
            </div>
          )}
          {living_area && (
            <div className="flex flex-col">
              <span className="text-xs text-slate-500 dark:text-slate-400 font-semibold mb-1">Sqft</span>
              <span className="text-sm font-bold text-navy-deep dark:text-white">
                {formatNumber(living_area)}{isEstimated('living_area') && <span className="text-xs text-slate-400 align-super">*</span>}
              </span>
            </div>
          )}
        </div>
        
        {/* Estimated features disclaimer */}
        {hasAnyEstimated && (
          <p className="text-xs text-slate-400 dark:text-slate-500 italic mb-3">
            *Estimated from neighbourhood statistics
          </p>
        )}

        {/* Property Type & Days on Market */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-800">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary capitalize">
            {property_type || 'Property'}
          </span>
          <div className="flex items-center gap-2">
            {calculatedDom !== null && (
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {calculatedDom} days on market
              </span>
            )}
            {is_relisted && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400" title="Relisted — motivated seller">
                <span className="material-symbols-outlined text-xs mr-0.5">autorenew</span>
                Relisted
              </span>
            )}
          </div>
        </div>

        {/* Community Vote Poll */}
        {prediction.property_id && (
          <ListingVoteWidget
            propertyId={prediction.property_id}
            listPrice={list_price}
            city={city}
            propertyType={property_type}
            compact
            initialAggregates={voteAggregates}
          />
        )}
      </div>
    </div>
  )
}
